import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ProfileTabs from "@/components/ProfileTabs";
import { toast } from "sonner";
import {
  AlertTriangle, Check, CheckCircle2, ChevronDown, Clock, Copy, Gift, Heart, Instagram, Loader2, Lock,
  Mail, MessageCircle, Music2, ShoppingBag, Smartphone, Sparkles, Star, UserCircle, Users, Camera, Cake,
} from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

// ---------- Types ----------
interface Tier { points: number; amount: number; min_order: number; best?: boolean }
interface PromoCode { code: string; amount: number; min_order: number; expires_at: string; status: "available" | "used" | "expired" }
interface EarnAction {
  key: "referral" | "birthday" | "review_photo" | "instagram_story" | "tiktok_story" | "instagram" | "tiktok" | "profile" | "signup";
  points: number; group?: string; done?: boolean; completed?: boolean; available_again_at?: string | null;
}
interface Completed { label: string; points: number; date: string }
interface Pending { label: string; points?: number; date?: string }
interface LoyaltyData {
  first_name: string; points: number;
  user_id?: number;
  expiring: { points: number; date: string } | null;
  tiers: Tier[]; codes: PromoCode[]; actions: EarnAction[]; completed: Completed[]; pending?: Pending[];
  referral_link: string; profile_completion: number;
}

// ---------- Données fictives ----------
const inDays = (d: number) => new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);
const MOCK: LoyaltyData = {
  first_name: "Camille",
  points: 230,
  expiring: { points: 40, date: "2027-01-15" },
  tiers: [
    { points: 150, amount: 5, min_order: 25 },
    { points: 280, amount: 10, min_order: 40 },
    { points: 400, amount: 15, min_order: 55 },
    { points: 500, amount: 20, min_order: 70, best: true },
  ],
  codes: [
    { code: "WMZ-5EUR-K8P2", amount: 5, min_order: 25, expires_at: inDays(5), status: "available" },
    { code: "WMZ-5EUR-A1Z9", amount: 5, min_order: 25, expires_at: "2026-06-01", status: "used" },
  ],
  actions: [
    { key: "referral", points: 50 },
    { key: "birthday", points: 20 },
    { key: "profile", points: 10, group: "Mon profil" },
    { key: "signup", points: 20, group: "Mon profil", completed: true },
    { key: "instagram_story", points: 100, group: "Mes réseaux", available_again_at: null },
    { key: "tiktok_story", points: 100, group: "Mes réseaux", available_again_at: null },
    { key: "review_photo", points: 20 },
    { key: "tiktok", points: 15, group: "Mes réseaux" },
    { key: "instagram", points: 15, group: "Mes réseaux", completed: true },
  ],
  completed: [
    { label: "Création de compte", points: 20, date: "2026-02-03" },
    { label: "Inscription à la newsletter", points: 10, date: "2026-02-03" },
    { label: "Abonnement Instagram", points: 15, date: "2026-03-12" },
    { label: "Avis produit", points: 30, date: "2026-05-20" },
  ],
  pending: [
    { label: "Story Instagram — @we.memberz", points: 100, date: "2026-10-06" },
    { label: "Story TikTok — @wememberz", points: 100, date: "2026-10-07" },
  ],
  referral_link: "https://www.wememberz.com/?parrain=CAMILLE230",
  profile_completion: 60,
};

// ---------- Utilitaires ----------
const fmtDate = (s: string) => new Date(s).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
const fmtEur = (n: number) => `${n.toLocaleString("fr-FR")} €`;
const csrf = () => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
async function post(url: string, body: Record<string, unknown>) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json().catch(() => ({}));
}
async function put(url: string, body: Record<string, unknown>) {
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json().catch(() => ({}));
}
const copy = (text: string, msg = "Code copié !") => {
  navigator.clipboard?.writeText(text).then(() => toast.success(msg), () => toast.error("Copie impossible"));
};
const daysUntil = (s: string) => Math.ceil((new Date(s).getTime() - Date.now()) / 86400000);

function useCountUp(target: number, ms = 900) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0; const start = performance.now(); const from = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / ms);
      setV(Math.round(from + (target - from) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

function fireConfetti() {
  const colors = ["hsl(var(--primary))", "hsl(var(--accent))", "#f9bb72", "#f6d27a"];
  const root = document.createElement("div");
  root.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden";
  for (let i = 0; i < 70; i++) {
    const p = document.createElement("span");
    const x = 50 + (Math.random() - 0.5) * 30;
    p.style.cssText = `position:absolute;left:${x}%;top:40%;width:8px;height:12px;border-radius:2px;background:${colors[i % colors.length]};opacity:1`;
    root.appendChild(p);
    const dx = (Math.random() - 0.5) * 700, dy = -200 - Math.random() * 300;
    p.animate(
      [{ transform: "translate(0,0) rotate(0)" }, { transform: `translate(${dx}px,${dy}px) rotate(${Math.random() * 720}deg)`, offset: 0.4 },
       { transform: `translate(${dx * 1.2}px,${dy + 700}px) rotate(${Math.random() * 1080}deg)`, opacity: 0 }],
      { duration: 1600 + Math.random() * 800, easing: "cubic-bezier(.2,.7,.4,1)", fill: "forwards" },
    );
  }
  document.body.appendChild(root);
  setTimeout(() => root.remove(), 2600);
}

const Card = ({ className, children }: { className?: string; children: React.ReactNode }) => (
  <section className={cn("rounded-3xl border border-border/60 bg-card p-5 md:p-6 shadow-sm", className)}>{children}</section>
);
const Title = ({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) => (
  <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-foreground">
    <Icon className="h-5 w-5 text-primary" />{children}
  </h2>
);

// ---------- Page ----------
export default function Avantages() {
  const location = useLocation();
  const [data, setData] = useState<LoyaltyData>(MOCK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let off = false;
    fetch("/profiles/avantages.json", { headers: { Accept: "application/json" } })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((j) => { if (!off) setData({ ...MOCK, ...j, pending: Array.isArray(j.pending) ? j.pending : [] }); })
      .catch(() => {})
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, []);

  // Toggle de test caché : ?debug_points=0|120|230|520
  const debug = new URLSearchParams(location.search).get("debug_points");
  const d: LoyaltyData = useMemo(() => {
    if (debug === null) return data;
    const pts = Number(debug) || 0;
    return { ...data, points: pts, codes: pts === 0 ? [] : data.codes, expiring: pts >= 40 ? data.expiring : null };
  }, [data, debug]);

  const [points, setPoints] = useState(d.points);
  const [codes, setCodes] = useState<PromoCode[]>(d.codes);
  useEffect(() => { setPoints(d.points); setCodes(d.codes); }, [d]);

  const reload = async () => {
    try {
      const r = await fetch("/profiles/avantages.json", { headers: { Accept: "application/json" } });
      if (r.ok) {
        const j = await r.json();
        setData({ ...MOCK, ...j, pending: Array.isArray(j.pending) ? j.pending : [] });
      }
    } catch { /* dev sans backend */ }
  };

  const tiers = [...d.tiers].sort((a, b) => a.points - b.points);
  const next = tiers.find((t) => t.points > points);
  const max = tiers[tiers.length - 1]?.points ?? 500;

  const [confirmTier, setConfirmTier] = useState<Tier | null>(null);
  const [unlocked, setUnlocked] = useState<{ tier: Tier; code: PromoCode } | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  const unlock = async () => {
    if (!confirmTier) return;
    setUnlocking(true);
    const tier = confirmTier;
    let code: PromoCode;
    try {
      const res = await post("/profiles/unlock_loyalty", { tier_points: tier.points });
      const raw = res?.code ?? fallbackCode(tier);
      // Votre serveur renvoie juste un "code" (chaîne) : on complète avec les infos du palier.
      code = typeof raw === "string"
        ? { code: raw, amount: tier.amount, min_order: tier.min_order, expires_at: inDays(91), status: "available" }
        : raw;
    } catch {
      code = fallbackCode(tier); // dev sans backend
    }
    setPoints(points - tier.points);
    setCodes((c) => [code, ...c]);
    setUnlocking(false);
    setConfirmTier(null);
    setUnlocked({ tier, code });
    fireConfetti();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-accent/10">
      <Navigation />
      <main className="container mx-auto px-4 pt-32 pb-16">
        <div className="mx-auto max-w-6xl">
          <ProfileTabs active="avantages" />
          {loading && (
            <p className="mb-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </p>
          )}
          <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
            {/* Colonne gauche */}
            <div className="space-y-6">
              <Balance name={d.first_name} points={points} tiers={tiers} next={next} max={max} expiring={d.expiring} />
              <Rewards tiers={tiers} points={points} onUnlock={setConfirmTier} />
              <Codes codes={codes} firstTier={tiers[0]} points={points} />
            </div>
            {/* Colonne droite */}
            <div className="space-y-6">
              <Done items={d.completed} />
              <Earn actions={d.actions} referral={d.referral_link} profile={d.profile_completion} userId={d.user_id} onRefresh={reload} />
              {d.pending && d.pending.length > 0 && <PendingList items={d.pending} />}
            </div>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm">
            <a href="/programme-fidelite" className="text-primary underline-offset-4 hover:underline">Comment fonctionne le programme ?</a>
          </div>
        </div>
      </main>
      <Footer />

      <AlertDialog open={!!confirmTier} onOpenChange={(o) => !o && setConfirmTier(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Échanger {confirmTier?.points} points contre un bon de {confirmTier && fmtEur(confirmTier.amount)} ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Votre bon sera valable 3 mois, dès {confirmTier && fmtEur(confirmTier.min_order)} d'achat.
              Il vous restera {confirmTier ? points - confirmTier.points : 0} points.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unlocking}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); unlock(); }} disabled={unlocking}>
              {unlocking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Débloquer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!unlocked} onOpenChange={(o) => !o && setUnlocked(null)}>
        <AlertDialogContent className="text-center">
          <AlertDialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15">
              <Gift className="h-7 w-7 text-primary" />
            </div>
            <AlertDialogTitle className="text-center">Bravo, votre bon de {unlocked && fmtEur(unlocked.tier.amount)} est prêt !</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              Valable jusqu'au {unlocked && fmtDate(unlocked.code.expires_at)}, dès {unlocked && fmtEur(unlocked.code.min_order)} d'achat.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-primary/50 bg-primary/5 p-3">
            <span className="font-mono text-lg font-bold tracking-wider">{unlocked?.code.code}</span>
            <Button size="sm" variant="outline" onClick={() => unlocked && copy(unlocked.code.code)}><Copy className="mr-1 h-4 w-4" />Copier</Button>
          </div>
          <AlertDialogFooter className="sm:justify-center">
            <AlertDialogAction onClick={() => setUnlocked(null)}>Fermer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function fallbackCode(t: Tier): PromoCode {
  const rnd = Math.random().toString(36).slice(2, 6).toUpperCase();
  return { code: `WMZ-${t.amount}EUR-${rnd}`, amount: t.amount, min_order: t.min_order, expires_at: inDays(91), status: "available" };
}

// ---------- 1. Solde ----------
function Balance({ name, points, tiers, next, max, expiring }: {
  name: string; points: number; tiers: Tier[]; next?: Tier; max: number; expiring: LoyaltyData["expiring"];
}) {
  const shown = useCountUp(points);
  const pct = Math.min(100, (points / max) * 100);
  return (
    <Card className="bg-gradient-to-br from-primary/10 via-card to-accent/10">
      <p className="text-lg text-muted-foreground">Bonjour {name}</p>
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <span className="text-5xl sm:text-6xl font-extrabold tabular-nums text-primary">{shown}</span>
          <span className="text-lg font-semibold text-primary">points</span>
        </div>
        <p className="whitespace-nowrap text-right text-xs sm:text-sm font-medium text-foreground">
          {next ? <>Plus que <strong className="text-primary">{next.points - points} points</strong> pour votre bon de <strong className="text-primary">{fmtEur(next.amount)}</strong></>
                : <>Vous avez atteint le palier maximum, bravo !</>}
        </p>
      </div>
      <div className="mt-8 mb-8">
        <div className="relative mr-7">
          {tiers.map((t) => {
            const reached = points >= t.points;
            return (
              <div key={t.points} className="absolute -top-6 -translate-x-1/2 text-center" style={{ left: `${(t.points / max) * 100}%` }}>
                <div className={cn("whitespace-nowrap text-xs leading-tight", reached ? "font-semibold text-primary" : "text-muted-foreground")}>
                  {t.points} pts
                </div>
              </div>
            );
          })}
          <div className="h-3 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all duration-1000" style={{ width: `${pct}%` }} />
          </div>
          {tiers.map((t) => {
            const reached = points >= t.points;
            return (
              <div key={t.points} className="absolute top-0 -translate-x-1/2 text-center" style={{ left: `${(t.points / max) * 100}%` }}>
                <div className={cn("h-3 w-3 rounded-full border-2 border-card", reached ? "bg-primary" : "bg-muted-foreground/40")} />
                <div className={cn("mt-1 whitespace-nowrap text-[10px] leading-tight", reached ? "font-semibold text-primary" : "text-muted-foreground")}>
                  {fmtEur(t.amount)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {expiring && expiring.points > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-accent/15 px-3 py-2 text-sm text-foreground">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" />
          <span>{expiring.points} points expirent le {fmtDate(expiring.date)}. Utilisez-les ou passez commande pour les conserver.</span>
        </div>
      )}
      <Button className="w-full sm:w-auto" asChild><a href="/social_networks">Crée un album</a></Button>
    </Card>
  );
}

// ---------- 2. Récompenses ----------
function Rewards({ tiers, points, onUnlock }: { tiers: Tier[]; points: number; onUnlock: (t: Tier) => void }) {
  const best = tiers.find((t) => t.best);
  const smallest = tiers[0];
  const showTip = best && smallest && points >= smallest.points && points < best.points;
  return (
    <Card>
      <Title icon={Gift}>Mes récompenses</Title>
      <div className="grid gap-3 sm:grid-cols-2">
        {tiers.map((t) => {
          const ok = points >= t.points;
          return (
            <div key={t.points} className={cn(
              "relative rounded-2xl border p-4 transition-all",
              ok ? "border-primary/40 bg-gradient-to-br from-primary/15 to-accent/15 shadow-md" : "border-border bg-muted/40",
            )}>
              {t.best && <Badge className="absolute -top-2 right-3">Meilleure valeur</Badge>}
              <div className="flex items-start justify-between">
                <div>
                  <p className={cn("text-3xl font-extrabold", ok ? "text-foreground" : "text-muted-foreground")}>{fmtEur(t.amount)}</p>
                </div>
                {ok ? <Sparkles className="h-5 w-5 text-primary" /> : <Lock className="h-5 w-5 text-muted-foreground" />}
              </div>
              <p className="mt-2 text-sm font-semibold text-foreground/80">{t.points} points</p>
              {ok ? (
                <Button size="sm" className="mt-3 w-full" onClick={() => onUnlock(t)}>Débloquer mon code</Button>
              ) : (
                <div className="mt-3">
                  <p className="mb-1 text-xs text-muted-foreground">Plus que {t.points - points} points</p>
                  <Progress value={(points / t.points) * 100} className="h-1.5" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showTip && (
        <p className="mt-4 rounded-xl bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
          Conseil : en attendant {best.points} points, vos points valent plus ({fmtEur(best.amount)} au lieu de 3 × {fmtEur(smallest.amount)}).
        </p>
      )}
    </Card>
  );
}

// ---------- 3. Codes ----------
function Codes({ codes, firstTier, points }: { codes: PromoCode[]; firstTier?: Tier; points: number }) {
  const label = { available: "Disponible", used: "Utilisé", expired: "Expiré" };
  return (
    <Card>
      <Title icon={Star}>Mes codes promo</Title>
      {codes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Vous n'avez pas encore de code.{" "}
          {firstTier && points < firstTier.points && <>Plus que {firstTier.points - points} points pour votre premier bon !</>}
        </p>
      ) : (
        <ul className="space-y-3">
          {codes.map((c) => {
            const soon = c.status === "available" && daysUntil(c.expires_at) < 7;
            const active = c.status === "available";
            return (
              <li key={c.code} className={cn("rounded-2xl border p-4", active ? "border-border" : "border-border/50 opacity-60")}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-bold">{c.amount}</span>
                    <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm tracking-wider">{c.code}</span>
                    {active && (
                      <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Copier le code" onClick={() => copy(c.code)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Badge variant={active ? "default" : "secondary"}>{label[c.status]}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>
                    Dès {fmtEur(c.min_order)} d'achat · expire le{" "}
                    <span className={cn(soon && "font-semibold text-orange-500")}>{fmtDate(c.expires_at)}</span>
                  </span>
                  {active && <Button size="sm" variant="outline" asChild><a href="/social_networks">Utiliser maintenant</a></Button>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ---------- 4. Gagner des points ----------
function VictoryBurst() {
  const colors = ["hsl(var(--stats-orange))", "hsl(var(--primary))", "hsl(var(--loyalty-accent))"];
  const bits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => {
    const angle = (i / 12) * Math.PI * 2;
    const dist = 34 + (i % 3) * 11;
    return {
      cx: `${Math.round(Math.cos(angle) * dist * 1.25)}px`,
      cy: `${Math.round(Math.sin(angle) * dist)}px`,
      cr: `${Math.round((i % 2 ? 1 : -1) * (120 + i * 20))}deg`,
      cd: `${i * 30}ms`,
    };
  });
  return (
    <span className="victory-burst" aria-hidden="true">
      {bits.map((b, i) => (
        <i key={i} style={{ "--cx": b.cx, "--cy": b.cy, "--cr": b.cr, "--cd": b.cd, background: colors[i % colors.length] } as React.CSSProperties} />
      ))}
    </span>
  );
}
function Earn({ actions, referral, profile, userId, onRefresh }: { actions: EarnAction[]; referral: string; profile: number; userId?: number; onRefresh: () => Promise<void> | void }) {
  const refresh = onRefresh;
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [bday, setBday] = useState(""); const [bdayOpen, setBdayOpen] = useState(false);
  const [bdaySaving, setBdaySaving] = useState(false);
  const [storyAction, setStoryAction] = useState<EarnAction | null>(null);
  const [pseudo, setPseudo] = useState("");
  const [storySaving, setStorySaving] = useState(false);
  const list = actions.filter((a) => !a.done); // dans l'ordre du load, pas de tri

  const confirmStory = async () => {
    if (!storyAction || !pseudo.trim()) return;
    setStorySaving(true);
    try {
      await post("/profiles/add_loyalty", { kind: storyAction.key, pseudo: pseudo.trim() });
      await refresh();
    } catch { /* dev */ }
    setDone((d) => ({ ...d, [storyAction.key]: true }));
    setStorySaving(false);
    setStoryAction(null);
  };

  const mark = async (key: string, url: string, body: Record<string, unknown> = {}) => {
    try { await post(url, body); await refresh(); } catch { /* dev */ }
    setDone((d) => ({ ...d, [key]: true }));
    toast.success("Merci ! Vos points seront crédités très vite.");
  };

  const saveBirthday = async () => {
    if (!bday) return;
    setBdaySaving(true);
    try {
      if (userId) {
        await put(`/profiles/${userId}`, { user: { birthday: bday } });
        await refresh();
        setDone((d) => ({ ...d, birthday: true }));
        toast.success("Date d'anniversaire enregistrée !");
        return;
      }
      await post("/loyalty/birthday", { birthday: bday }); // dev sans backend
    } catch { /* on garde l'état fait localement */ }
    setBdaySaving(false);
    setDone((d) => ({ ...d, birthday: true }));
    toast.success("Date d'anniversaire enregistrée ! Vos points seront crédités très vite.");
  };
  const msg = encodeURIComponent(`Je t'offre une réduction sur ton premier album Wememberz : ${referral}`);

  const meta: Record<EarnAction["key"], { icon: React.ElementType; title: string; text: string }> = {
    referral: { icon: Users, title: "Parraine un proche", text: "Ton proche profite d'une réduction et tu gagnes des points à sa première commande." },
    birthday: { icon: Cake, title: "Renseigne ta date d'anniversaire", text: "Nous te réservons une petite surprise le jour J." },
    review_photo: { icon: Camera, title: "Ajoute une photo à ton avis", text: "Montre ton album reçu : cela aide beaucoup les autres familles." },
    instagram_story: { icon: Instagram, title: "Identifie @we.memberz sur Instagram", text: "Partage ton album en story (1 fois par mois)." },
    tiktok_story: { icon: Music2, title: "Identifie @wememberz sur TikTok", text: "Partage ton album en vidéo TikTok (1 fois par mois)." },
    tiktok: { icon: Music2, title: "Suis Wememberz sur TikTok", text: "Des idées d'albums et des coulisses chaque semaine." },
    instagram: { icon: Instagram, title: "Suis Wememberz sur Instagram", text: "Nos plus beaux albums et l'inspiration au quotidien." },
    profile: { icon: UserCircle, title: "Complète ton profil", text: "Quelques informations pour mieux t'accompagner." },
    signup: { icon: Sparkles, title: "Bienvenue chez Wememberz", text: "Ton compte est créé : les points de bienvenue s'invitent." },
  };

  const others = list.filter((a) => !a.group); // ordre du load, pas de tri
  const groupNames: string[] = [];
  for (const a of list) if (a.group && !groupNames.includes(a.group)) groupNames.push(a.group);
  const groupedBy: Record<string, EarnAction[]> = {};
  for (const a of list) if (a.group) (groupedBy[a.group] ??= []).push(a);

  const actionArea = (a: EarnAction, isDone: boolean) => {
    if (isDone) {
      return (
        <Badge variant="outline" className="mt-2 inline-flex gap-1 border-green-600/40 bg-green-50 text-green-700">
          <Check className="h-3 w-3" />Réalisée
        </Badge>
      );
    }
    if (a.key === "referral") {
      return (
        <div className="mt-3 space-y-2">
          <div className="flex gap-2">
            <Input readOnly value={referral} className="font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
            <Button size="sm" onClick={() => copy(referral, "Lien copié !")}><Copy className="mr-1 h-4 w-4" />Copier</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" asChild><a href={`https://wa.me/?text=${msg}`} target="_blank" rel="noreferrer"><MessageCircle className="mr-1 h-4 w-4" />WhatsApp</a></Button>
            <Button size="sm" variant="outline" asChild><a href={`mailto:?subject=${encodeURIComponent("Un cadeau Wememberz pour vous")}&body=${msg}`}><Mail className="mr-1 h-4 w-4" />E-mail</a></Button>
            <Button size="sm" variant="outline" asChild><a href={`sms:?&body=${msg}`}><Smartphone className="mr-1 h-4 w-4" />SMS</a></Button>
          </div>
        </div>
      );
    }
    if (a.key === "birthday") {
      return bdayOpen ? (
        <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); saveBirthday(); }}>
          <Input type="date" value={bday} onChange={(e) => setBday(e.target.value)} required className="max-w-[180px]" />
          <Button size="icon" type="submit" disabled={bdaySaving} aria-label="Enregistrer">
            {bdaySaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
        </form>
      ) : <Button size="sm" variant="outline" className="mt-3" onClick={() => setBdayOpen(true)}>Renseigne ma date</Button>;
    }
    if (a.key === "review_photo") {
      return <Button size="sm" variant="outline" className="mt-3" asChild><a href="/profiles/commandes">Ajoute une photo</a></Button>;
    }
    if (a.key === "instagram_story" || a.key === "tiktok_story") {
      return a.available_again_at ? (
        <p className="mt-2 text-sm text-muted-foreground">Disponible à nouveau le {fmtDate(a.available_again_at)}</p>
      ) : (
        <Button size="sm" variant="outline" className="mt-3" onClick={() => { setPseudo(""); setStoryAction(a); }}>J'ai partagé ma story</Button>
      );
    }
    if (a.key === "tiktok") {
      return (
        <Button size="sm" variant="outline" className="mt-3" asChild>
          <a
            href="https://www.tiktok.com/@wememberz"
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              setDone((d) => ({ ...d, tiktok: true }));
              post("/profiles/add_loyalty", { kind: "tiktok" }).then(() => refresh()).catch(() => {});
            }}
          >Ouvrir TikTok</a>
        </Button>
      );
    }
    if (a.key === "instagram") {
      return (
        <Button size="sm" variant="outline" className="mt-3" asChild>
          <a
            href="https://www.instagram.com/we.memberz/"
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              setDone((d) => ({ ...d, instagram: true }));
              post("/profiles/add_loyalty", { kind: "instagram" }).then(() => refresh()).catch(() => {});
            }}
          >Ouvrir Instagram</a>
        </Button>
      );
    }
    if (a.key === "signup") {
      return <p className="mt-2 text-sm text-muted-foreground">Points offerts automatiquement à ton inscription.</p>;
    }
    return (
      <div className="mt-3 space-y-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Progress value={profile} className="h-1.5 flex-1" /> Profil complété à {profile} %
        </div>
        <Button size="sm" variant="outline" asChild><a href="/profiles/new">Complète mon profil</a></Button>
      </div>
    );
  };

  return (
    <Card>
      <Title icon={Heart}>Gagne plus de points</Title>
      <div className="space-y-3">
        <div className="rounded-2xl bg-primary/10 p-4">
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-5 w-5 shrink-0 text-primary" />
            <p className="text-sm text-foreground"><strong>Chaque commande</strong> : 1 point par euro dépensé, automatiquement.</p>
          </div>
          <div className="mt-2 flex items-start gap-3">
            <Star className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <p className="text-sm font-semibold text-foreground">Points doublés pour votre 1<sup>re</sup> commande : 2 points par euro{"\u00A0"}!</p>
          </div>
        </div>
        {others.map((a) => {
          const m = meta[a.key]; const Icon = m.icon; const isDone = done[a.key] || a.completed;
          const featured = !isDone && a.key === "referral";
          return (
            <div key={a.key} className={cn(
              "relative rounded-2xl border p-4 transition-all duration-200",
              isDone ? "border-green-600/40 bg-green-50" : featured ? "border-primary/40 bg-primary/5" : "border-border",
              !isDone && "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md",
              done[a.key] && "action-just-completed",
            )}>
              {done[a.key] && <VictoryBurst />}
              <div className="flex items-start gap-3">
                <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", isDone ? "bg-green-100 text-green-700" : featured ? "bg-primary text-primary-foreground" : "bg-muted text-primary")}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className={cn("font-semibold", isDone ? "text-green-800" : "text-foreground")}>{m.title}</p>
                    <Badge variant={featured ? "default" : "secondary"}>+{a.points} pts</Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{m.text}</p>
                  {actionArea(a, isDone)}
                </div>
              </div>
            </div>
          );
        })}
        {groupNames.map((g) => (
          <div key={g}>
            <p className="mb-2 mt-1 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <Sparkles className="h-4 w-4 text-primary" />{g}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {groupedBy[g].map((a) => {
                const m = meta[a.key]; const Icon = m.icon; const isDone = done[a.key] || a.completed;
                return (
                  <div key={a.key} className={cn(
                    "relative flex flex-col rounded-2xl border p-4 transition-all duration-200",
                    isDone ? "border-green-600/40 bg-green-50" : "border-border",
                    !isDone && "hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md",
                    done[a.key] && "action-just-completed",
                  )}>
                    {done[a.key] && <VictoryBurst />}
                    <div className="flex items-start justify-between gap-2">
                      <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", isDone ? "bg-green-100 text-green-700" : "bg-muted text-primary")}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <Badge variant="secondary" className="shrink-0">+{a.points} pts</Badge>
                    </div>
                    <p className={cn("mt-2 text-sm font-semibold leading-snug", isDone ? "text-green-800" : "text-foreground")}>{m.title}</p>
                    <div className="mt-auto pt-3">{actionArea(a, isDone)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <AlertDialog open={!!storyAction} onOpenChange={(open) => { if (!open && !storySaving) setStoryAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>As-tu bien identifié {storyAction?.key === "tiktok_story" ? "@wememberz" : "@we.memberz"} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Un membre de notre équipe va regarder ta story pour valider tes points. Pour qu'on puisse te retrouver, indique-nous ton pseudo {storyAction?.key === "tiktok_story" ? "TikTok" : "Instagram"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            autoFocus
            placeholder={storyAction?.key === "tiktok_story" ? "Ton pseudo TikTok" : "Ton pseudo Instagram"}
            value={pseudo}
            onChange={(e) => setPseudo(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmStory(); } }}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={storySaving}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmStory(); }} disabled={!pseudo.trim() || storySaving}>
              {storySaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Valider
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ---------- 5. En cours de validation ----------
function PendingList({ items }: { items: Pending[] }) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-xl font-bold text-foreground">
          <Clock className="h-5 w-5 text-amber-600" />En cours de validation
        </span>
        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">{items.length}</Badge>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Un membre de l'équipe vérifie vos actions avant de créditer les points.
      </p>
      <ul className="mt-4 divide-y divide-border">
        {items.map((i, idx) => (
          <li key={`${i.label}-${idx}`} className="flex items-center justify-between gap-2 py-2 text-sm">
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-amber-600" />{i.label}
            </span>
            <span className="text-right">
              {typeof i.points === "number" && (
                <span className="font-semibold text-amber-600">+{i.points} pts</span>
              )}
              {i.date && <span className={cn("text-xs text-muted-foreground", typeof i.points === "number" && "ml-2")}>{fmtDate(i.date)}</span>}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

// ---------- 6. Déjà gagnés ----------
function Done({ items }: { items: Completed[] }) {
  const [open, setOpen] = useState(false);
  const total = items.reduce((s, i) => s + i.points, 0);
  return (

    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center justify-between">
          <span className="flex items-center gap-2 text-xl font-bold text-foreground">
            <CheckCircle2 className="h-5 w-5 text-primary" />Déjà gagnés
          </span>
          <span className="flex items-center gap-3 whitespace-nowrap">
            <span className="text-sm text-muted-foreground">
              {items.length} actions · <span className={cn("font-semibold", total >= 0 ? "text-green-600" : "text-red-600")}>{total >= 0 ? "+" : ""}{total} pts</span>
            </span>
            <ChevronDown className={cn("h-5 w-5 transition-transform", open && "rotate-180")} />
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="mt-4 divide-y divide-border">
            {items.map((i) => (
              <li key={i.label} className="flex items-center justify-between gap-2 py-2 text-sm">
                <span className="flex items-center gap-2"><Check className="h-4 w-4 text-green-600" />{i.label}</span>
                <span className="text-right">
                  <span className={cn("font-semibold", i.points >= 0 ? "text-green-600" : "text-red-600")}>{i.points >= 0 ? "+" : ""}{i.points}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{fmtDate(i.date)}</span>
                </span>
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
